/**
 * AppointmentDateDialog
 *
 * Multi-step panel for managing appointment dates across a patient's assessment
 * modules in one session (ADHD: CI, SSI, DD, PAD; Adult Autism: DDI, OBS, DD,
 * PAD). Pathway differences are injected via props — see predictRefinalisation /
 * rerenderReports below. Mirrors the edit → confirm →
 * success pattern used by ProviderEditPatientForm so clinicians can:
 *   1. Draft several module date changes locally
 *   2. Preview before / after on a confirm screen
 *   3. Commit once (single Firestore write + one PR re-finalise)
 *
 * Steps:
 *   edit    — clean form: one always-visible date field per module
 *   confirm — before → after diff for every pending change
 *   success — confirmation that dates were saved (reports re-finalise in bg)
 *
 * Dates are stored as calendar YYYY-MM-DD strings (not Timestamp instants)
 * so report rendering never shifts the day across London DST boundaries.
 * Uses native <input type="date"> to match PatientInfoPanel / PatientEditForm.
 *
 * Props:
 *   isOpen             – boolean controlling dialog visibility
 *   onClose            – callback to close the dialog
 *   patientId          – Firestore patient document ID
 *   appointmentDates   – object from patientDataModel.appointmentDates (may be {})
 *   inferredDates      – optional shortKey → YYYY-MM-DD map of completion-inferred
 *                        dates (when no human-set date exists). Shown and editable
 *                        in the dialog; not written until the user acts.
 *   moduleOrder        – array from config.moduleOrder (respects SSI-first orgs)
 *   modules            – object from config.modules (for display titles)
 *   refreshPatientData – callback to re-fetch patientDataModel after save
 *   initialModule      – optional module short key to focus that date input
 *   editableModules    – optional Set/array of short keys that may be edited.
 *                        Disabled/N/A modules are excluded so their dates stay
 *                        hidden and cannot be set from this dialog.
 *   organisation       – org display name (for Patient Report required-sections)
 *   manageReports      – dataModel['6_manageReports'] for predicting which
 *                        reports will actually re-finalise after save
 *   documentsRoute     – path for the Documents Dashboard CTA on success when
 *                        at least one report section will be re-finalised
 *   predictRefinalisation – optional `(moduleKeys) => { carsToRefinalise,
 *                        willRefinalisePatientReport, willRefinaliseAny }`.
 *                        Lets a non-ADHD pathway (Adult Autism) supply its own
 *                        prediction. Defaults to the ADHD helper over
 *                        `manageReports`.
 *   rerenderReports    – optional `({ patientId, moduleKeys, clinicianId,
 *                        organisation }) => Promise` run fire-and-forget after
 *                        a successful save. Defaults to the ADHD
 *                        rerenderAfterAppointmentDateChange.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { XIcon, CalendarIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/solid';

import WarningPanel from '../../../../components/informantManagement/WarningPanel';
import {
  applyAppointmentDateChanges,
  toCalendarDateString,
  formatAppointmentDateDisplay,
  MODULE_KEY_TO_SHORT_KEY,
  SHORT_KEY_LABELS,
  SHORT_KEY_DATE_LABELS,
} from '../../../../utils/appointmentDateHelpers';
import {
  predictAppointmentDateReportRefinalisation,
  rerenderAfterAppointmentDateChange,
} from '../../../../utils/rerenderAfterAppointmentDateChange';
import { auth } from '../../../../firebase';

// MODULE_KEY_TO_SHORT_KEY / SHORT_KEY_LABELS / SHORT_KEY_DATE_LABELS are shared
// with every Patient Overview that mounts this dialog (ADHD, CYP ADHD, Adult
// Autism) and live in appointmentDateHelpers so the pathways cannot drift.

const dateLabelFor = (shortKey) => SHORT_KEY_DATE_LABELS[shortKey] || 'appointment date';

/**
 * Default re-finalisation strategy: the ADHD helpers.
 *
 * The dialog itself is pathway-agnostic — it only knows about short keys and
 * calendar dates. What differs per pathway is (a) how to predict which PDFs
 * will be re-rendered and (b) how to kick those renders off, because the
 * approval flags and render Cloud Functions live in different places for ADHD
 * (`dataModel.6_manageReports`) and Autism (`reportContent/{stage}`). Callers
 * inject their own pair via the `predictRefinalisation` / `rerenderReports`
 * props; when they do not, these ADHD defaults keep the existing ADHD and CYP
 * overviews working unchanged.
 */
const buildDefaultPredictRefinalisation = (manageReports) => (moduleKeys) =>
  predictAppointmentDateReportRefinalisation({ manageReports, moduleKeys });

const defaultRerenderReports = ({ patientId, moduleKeys, clinicianId, organisation }) =>
  rerenderAfterAppointmentDateChange({ patientId, moduleKeys, clinicianId, organisation });

/**
 * Inline spinner SVG used during confirm submit.
 * Exact copy from ReassignCliniciansDialog / ProviderEditPatientForm.
 */
const SpinnerSVG = ({ className = 'h-3 w-3 text-white' }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Build a shortKey → YYYY-MM-DD|null map from the saved appointmentDates object.
 * null means "not set" so draft diffs stay simple string/null comparisons.
 */
function snapshotDates(appointmentDates, shortKeys) {
  const snap = {};
  for (const shortKey of shortKeys) {
    snap[shortKey] = toCalendarDateString(appointmentDates?.[shortKey]?.date) || null;
  }
  return snap;
}

/**
 * Diff draft vs baseline. Returns an array of pending changes ready for
 * applyAppointmentDateChanges / the confirm screen.
 */
function computePendingChanges(baseline, draft, shortKeys) {
  const changes = [];
  for (const shortKey of shortKeys) {
    const previousDate = baseline[shortKey] ?? null;
    const newDate = draft[shortKey] ?? null;
    if (previousDate === newDate) continue;
    changes.push({
      moduleKey: shortKey,
      previousDate,
      newDate,
      action: newDate == null ? 'removed' : (previousDate ? 'updated' : 'set'),
    });
  }
  return changes;
}

function formatDateOrNotSet(calendarDate) {
  if (!calendarDate) return 'Not set';
  return formatAppointmentDateDisplay(calendarDate, 'd MMM yyyy') || 'Not set';
}

function AppointmentDateDialog({
  isOpen,
  onClose,
  patientId,
  appointmentDates = {},
  inferredDates = {},
  moduleOrder = [],
  modules = {},
  refreshPatientData,
  initialModule = null,
  editableModules = null,
  organisation = null,
  manageReports = {},
  documentsRoute = null,
  predictRefinalisation = null,
  rerenderReports = null,
}) {
  const navigate = useNavigate();

  // Resolve the pathway strategy once per render. Falling back to the ADHD
  // helpers here (rather than in the default-props list) lets the ADHD default
  // close over the live `manageReports` prop.
  const predictFn = useMemo(
    () => predictRefinalisation || buildDefaultPredictRefinalisation(manageReports),
    [predictRefinalisation, manageReports]
  );
  const rerenderFn = rerenderReports || defaultRerenderReports;

  // 'edit' | 'confirm' | 'success'
  const [currentStep, setCurrentStep] = useState('edit');

  // Snapshot of saved dates when the dialog opened (for before/after diffs)
  const [baselineDates, setBaselineDates] = useState({});
  // Local draft — only written to Firestore on Confirm
  const [draftDates, setDraftDates] = useState({});

  // Module to focus when the dialog opens from a specific card's date control
  const [focusModule, setFocusModule] = useState(null);
  const dateInputRefs = useRef({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Changes that were just confirmed — kept for the success screen copy
  const [committedChanges, setCommittedChanges] = useState([]);

  // Track which patient session we initialised for so a post-save refresh
  // does not tear away the success screen (same idea as ProviderEditPatientForm).
  const [initialisedForPatientId, setInitialisedForPatientId] = useState(null);

  // Modules the clinician may schedule. When omitted, all modules in
  // moduleOrder are editable (tests / callers that don't gate by disabled).
  const editableSet = useMemo(() => {
    if (editableModules == null) return null;
    return new Set(
      Array.isArray(editableModules) ? editableModules : [...editableModules]
    );
  }, [editableModules]);

  const visibleModuleOrder = useMemo(() => {
    return moduleOrder.filter((moduleKey) => {
      const shortKey = MODULE_KEY_TO_SHORT_KEY[moduleKey];
      if (!shortKey) return false;
      if (editableSet == null) return true;
      return editableSet.has(shortKey);
    });
  }, [moduleOrder, editableSet]);

  const visibleShortKeys = useMemo(
    () => visibleModuleOrder.map((k) => MODULE_KEY_TO_SHORT_KEY[k]).filter(Boolean),
    [visibleModuleOrder]
  );

  const moduleTitleFor = useCallback((shortKey, fullModuleKey) => {
    if (fullModuleKey && modules[fullModuleKey]?.title) {
      return modules[fullModuleKey].title;
    }
    return SHORT_KEY_LABELS[shortKey] || shortKey;
  }, [modules]);

  /**
   * Initialise / reset draft state when the dialog opens for a patient.
   * We intentionally do NOT re-snapshot on every appointmentDates prop change
   * while the dialog is open — that would wipe in-progress drafts after a
   * refresh, and would yank the user off the success step.
   */
  useEffect(() => {
    if (isOpen && patientId) {
      const isNewSession = initialisedForPatientId !== patientId;

      if (isNewSession) {
        const snap = snapshotDates(appointmentDates, visibleShortKeys);
        setBaselineDates(snap);
        setDraftDates(snap);
        setCurrentStep('edit');
        setCommittedChanges([]);
        setSubmitError(null);
        setIsSubmitting(false);
        setInitialisedForPatientId(patientId);
        setFocusModule(
          initialModule && visibleShortKeys.includes(initialModule)
            ? initialModule
            : null
        );
      }
    }

    if (!isOpen) {
      setInitialisedForPatientId(null);
      setFocusModule(null);
    }
  }, [
    isOpen,
    patientId,
    appointmentDates,
    visibleShortKeys,
    initialModule,
    initialisedForPatientId,
  ]);

  // Focus the date input for the module the clinician clicked on the card.
  useEffect(() => {
    if (!isOpen || currentStep !== 'edit' || !focusModule) return;
    const input = dateInputRefs.current[focusModule];
    if (input) {
      // Small delay so the dialog portal is mounted before focusing.
      const timer = setTimeout(() => {
        input.focus();
        // showPicker is supported in Chromium; ignore if unavailable.
        if (typeof input.showPicker === 'function') {
          try {
            input.showPicker();
          } catch {
            // Some browsers throw if showPicker is called without a user gesture.
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentStep, focusModule]);

  const pendingChanges = useMemo(
    () => computePendingChanges(baselineDates, draftDates, visibleShortKeys),
    [baselineDates, draftDates, visibleShortKeys]
  );

  const hasPendingChanges = pendingChanges.length > 0;

  // Predict re-finalisation from the same gates the background re-render uses.
  // On the success step we read committedChanges (pending draft was already saved);
  // on confirm we use the live pending list.
  const reportRefinalisation = useMemo(() => {
    const moduleKeys = (
      currentStep === 'success' ? committedChanges : pendingChanges
    ).map((change) => change.moduleKey);
    const predicted = predictFn(moduleKeys) || {};
    // Normalise so a partial strategy result can never crash the render.
    return {
      carsToRefinalise: predicted.carsToRefinalise || [],
      willRefinalisePatientReport: !!predicted.willRefinalisePatientReport,
      willRefinaliseAny: !!predicted.willRefinaliseAny,
    };
  }, [currentStep, committedChanges, pendingChanges, predictFn]);

  /**
   * Update a module's draft date as soon as the native picker changes.
   * Empty string from the input means "cleared" → null in draft.
   */
  const handleDraftDateChange = useCallback((shortKey, value) => {
    const next = value ? toCalendarDateString(value) : null;
    setDraftDates((prev) => ({ ...prev, [shortKey]: next }));
  }, []);

  /**
   * Clear a module's draft date (still local until Confirm).
   */
  const clearDraftDate = useCallback((shortKey) => {
    setDraftDates((prev) => ({ ...prev, [shortKey]: null }));
  }, []);

  /**
   * Promote an inferred (completion-based) date into the draft. This is a
   * deliberate human action — until Confirm it only marks the row pending;
   * it does not write Firestore by itself.
   */
  const useInferredDate = useCallback((shortKey, inferredDate) => {
    if (!inferredDate) return;
    setDraftDates((prev) => ({ ...prev, [shortKey]: inferredDate }));
  }, []);

  const goToConfirm = useCallback(() => {
    if (!hasPendingChanges) return;
    setSubmitError(null);
    setCurrentStep('confirm');
  }, [hasPendingChanges]);

  const goBackToEdit = useCallback(() => {
    setCurrentStep('edit');
    setSubmitError(null);
  }, []);

  /**
   * Commit every pending draft change in one Firestore write, refresh the
   * patient overview, then kick off report re-finalisation once for the
   * whole session (all changed CARs + a single Patient Report attempt).
   */
  const handleConfirm = useCallback(async () => {
    if (isSubmitting || pendingChanges.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await applyAppointmentDateChanges(
        patientId,
        pendingChanges.map(({ moduleKey, newDate, previousDate }) => ({
          moduleKey,
          newDate,
          previousDate,
        }))
      );

      if (refreshPatientData) await refreshPatientData();

      const changedModuleKeys = pendingChanges.map((c) => c.moduleKey);
      setCommittedChanges(pendingChanges);
      setCurrentStep('success');

      // Fire-and-forget: CARs for each changed module, then one PR re-render,
      // via whichever pathway strategy the caller injected (ADHD by default).
      Promise.resolve(
        rerenderFn({
          patientId,
          moduleKeys: changedModuleKeys,
          clinicianId: auth.currentUser?.uid,
          organisation,
        })
      ).catch((err) => {
        console.error('Failed to re-finalise reports after appointment date change:', err);
      });
    } catch (err) {
      console.error('Failed to save appointment dates:', err);
      setSubmitError('Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    pendingChanges,
    patientId,
    refreshPatientData,
    organisation,
    rerenderFn,
  ]);

  /**
   * Close the dialog. When reports are being re-finalised, also take the
   * clinician to the Documents Dashboard so they can watch processing and
   * download the latest PDFs — the vague "in the background" copy alone was
   * easy to miss.
   */
  const handleDone = useCallback(() => {
    const shouldGoToDocuments =
      reportRefinalisation.willRefinaliseAny && !!documentsRoute;
    onClose();
    if (shouldGoToDocuments) {
      navigate(documentsRoute);
    }
  }, [
    onClose,
    navigate,
    documentsRoute,
    reportRefinalisation.willRefinaliseAny,
  ]);

  // ─── Render: Edit screen ─────────────────────────────────────────────────
  // Flat form layout: module title + large readable date + always-visible
  // native date input. No expand/collapse Edit chrome.

  const renderEdit = () => (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900 pr-6">Manage Appointment Dates</h2>
        <p className="mt-1 text-xs text-gray-500">
          Update any modules below, then review and confirm to save.
        </p>
      </div>

      {visibleModuleOrder.length === 0 ? (
        <p className="text-xs text-gray-400 italic text-center py-6 border border-dashed border-gray-200 rounded-md">
          No applicable modules to schedule.
        </p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
          {visibleModuleOrder.map((moduleKey) => {
            const shortKey = MODULE_KEY_TO_SHORT_KEY[moduleKey];
            const moduleTitle = moduleTitleFor(shortKey, moduleKey);
            const draftDate = draftDates[shortKey] ?? null;
            const baselineDate = baselineDates[shortKey] ?? null;
            const inferredDate = toCalendarDateString(inferredDates?.[shortKey]) || null;
            // Show inferred when nothing is stored/drafted yet — visual only
            // until the user clicks "Use estimated date" or edits the input.
            const showingInferred = !draftDate && !baselineDate && !!inferredDate;
            const displayDate = draftDate || (showingInferred ? inferredDate : null);
            const isDirty = draftDate !== baselineDate;
            const inputId = `appointment-date-${shortKey}`;
            // Prefill the native input with the inferred day so it's editable,
            // but draftDates stays null until the user acts (onChange / Use).
            const inputValue = draftDate || (showingInferred ? inferredDate : '') || '';

            return (
              <div
                key={shortKey}
                className={`px-3.5 py-3 ${isDirty ? 'bg-violet-50/40' : 'bg-white'}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {moduleTitle}
                    </p>
                    {/* Display date is the visual anchor — larger than the
                        module label. Inferred dates are italic + labelled so
                        they are not mistaken for a human-confirmed appointment. */}
                    {displayDate ? (
                      <div className="mt-0.5">
                        <p className={`text-base font-semibold flex items-center gap-1.5 ${
                          showingInferred ? 'text-gray-600 italic' : 'text-gray-900'
                        }`}>
                          <CalendarIcon className="h-4 w-4 text-violet-600 flex-shrink-0" aria-hidden="true" />
                          <span>{formatAppointmentDateDisplay(displayDate, 'EEE, d MMM yyyy')}</span>
                        </p>
                        {showingInferred && (
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            Estimated from completion
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="mt-0.5 text-base font-medium text-gray-400 italic">
                        Not set
                      </p>
                    )}
                  </div>

                  {isDirty && (
                    <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">
                      Pending
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    ref={(el) => {
                      dateInputRefs.current[shortKey] = el;
                    }}
                    id={inputId}
                    type="date"
                    value={inputValue}
                    onChange={(e) => handleDraftDateChange(shortKey, e.target.value)}
                    aria-label={`${moduleTitle} ${dateLabelFor(shortKey)}`}
                    className="w-44 max-w-full flex-shrink-0 p-2 text-sm border border-gray-300 rounded-md bg-white
                      focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  />
                  {showingInferred && (
                    <button
                      type="button"
                      onClick={() => useInferredDate(shortKey, inferredDate)}
                      className="flex-shrink-0 px-2.5 py-2 text-xs font-semibold text-violet-700
                        hover:bg-violet-50 rounded-md transition-colors whitespace-nowrap"
                      aria-label={`Use estimated date for ${moduleTitle}`}
                    >
                      Use estimated date
                    </button>
                  )}
                  {(draftDate || baselineDate) && !showingInferred && (
                    <button
                      type="button"
                      onClick={() => clearDraftDate(shortKey)}
                      className="flex-shrink-0 px-2.5 py-2 text-xs font-semibold text-gray-600
                        hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label={`Clear ${moduleTitle} ${dateLabelFor(shortKey)}`}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <WarningPanel variant="info">
        <p>
          Only report sections that have already been approved and finalised will
          be re-rendered after you confirm (so the new dates appear in those PDFs).
          Sections still awaiting AI review are left unchanged until they are approved.
        </p>
      </WarningPanel>

      <div className="flex justify-end space-x-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={goToConfirm}
          disabled={!hasPendingChanges}
          className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
            hasPendingChanges
              ? 'bg-violet-600 hover:bg-violet-700'
              : 'bg-gray-400 cursor-not-allowed opacity-50'
          }`}
        >
          Review changes
        </button>
      </div>
    </div>
  );

  // ─── Render: Confirm screen ──────────────────────────────────────────────

  const renderConfirm = () => {
    if (pendingChanges.length === 0) {
      return (
        <div className="space-y-3">
          <p className="text-xs text-gray-600">No changes to confirm.</p>
          <button
            type="button"
            onClick={goBackToEdit}
            className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
          >
            &larr; Back to editing
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={goBackToEdit}
          disabled={isSubmitting}
          className="text-xs text-violet-600 hover:text-violet-800 font-semibold disabled:opacity-50"
        >
          &larr; Back to editing
        </button>

        <h3 className="text-base font-semibold text-gray-900">Confirm Changes</h3>

        <p className="text-xs text-gray-600">
          Please review the date changes below. Confirming will save them all at once
          {reportRefinalisation.willRefinaliseAny
            ? ' and re-finalise the report sections listed below.'
            : '.'}
        </p>

        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
          {pendingChanges.map((change) => {
            const fullKey = Object.keys(MODULE_KEY_TO_SHORT_KEY).find(
              (k) => MODULE_KEY_TO_SHORT_KEY[k] === change.moduleKey
            );
            const title = moduleTitleFor(change.moduleKey, fullKey);
            return (
              <div key={change.moduleKey} className="text-xs">
                <span className="font-medium text-gray-700">{title}:</span>
                <div className="flex flex-wrap items-center gap-x-2 mt-0.5 ml-2">
                  <span className="text-red-600 line-through break-all">
                    {formatDateOrNotSet(change.previousDate)}
                  </span>
                  <span className="text-gray-400">&rarr;</span>
                  <span className="text-green-700 font-medium break-all">
                    {formatDateOrNotSet(change.newDate)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Only warn about re-finalisation when the same gates as the background
            re-render helper say a CAR and/or Patient Report will actually run.
            Purple "Ready for review" (generated but not approved) and locked
            modules must not imply a re-finalise that will not happen. */}
        {reportRefinalisation.willRefinaliseAny ? (
          <WarningPanel variant="warning">
            <ul className="list-disc pl-4 space-y-1">
              {reportRefinalisation.carsToRefinalise.length > 0 && (
                <li>
                  Clinical Assessment Report
                  {reportRefinalisation.carsToRefinalise.length === 1 ? '' : 's'} for{' '}
                  {reportRefinalisation.carsToRefinalise
                    .map((shortKey) => {
                      const fullKey = Object.keys(MODULE_KEY_TO_SHORT_KEY).find(
                        (k) => MODULE_KEY_TO_SHORT_KEY[k] === shortKey
                      );
                      return moduleTitleFor(shortKey, fullKey);
                    })
                    .join(', ')}{' '}
                  will be re-finalised (already approved).
                </li>
              )}
              {reportRefinalisation.willRefinalisePatientReport && (
                <li>
                  The Patient Report will be re-finalised once for this session
                  (already available and will become stale after these date changes).
                </li>
              )}
            </ul>
          </WarningPanel>
        ) : (
          <WarningPanel variant="info">
            <p>
              No already-approved Clinical Assessment Reports or Patient Report
              will be re-generated for these changes. Dates will still be saved
              and used when those reports are finalised later. Sections still
              awaiting AI review are not affected.
            </p>
          </WarningPanel>
        )}

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-xs text-red-700">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={goBackToEdit}
            disabled={isSubmitting}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {isSubmitting && <SpinnerSVG />}
            <span>{isSubmitting ? 'Saving...' : 'Confirm changes'}</span>
          </button>
        </div>
      </div>
    );
  };

  // ─── Render: Success screen ──────────────────────────────────────────────

  const renderSuccess = () => {
    const count = committedChanges.length;
    const goToDocuments =
      reportRefinalisation.willRefinaliseAny && !!documentsRoute;

    return (
      <div className="space-y-3">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleSolid className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-base font-semibold text-gray-900">
            Dates Updated
          </h3>
          <p className="text-xs text-gray-600">
            {count === 1
              ? '1 date has been saved successfully.'
              : `${count} dates have been saved successfully.`}
            {reportRefinalisation.willRefinaliseAny ? (
              <>
                {' '}
                Already-approved report sections that use these dates are being
                re-finalised in the background. Please check the Documents
                Dashboard and download the latest versions once processing finishes.
              </>
            ) : (
              <>
                {' '}
                No already-approved reports needed re-generating for these changes.
              </>
            )}
          </p>
        </div>

        {committedChanges.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2 text-left">
            {committedChanges.map((change) => {
              const fullKey = Object.keys(MODULE_KEY_TO_SHORT_KEY).find(
                (k) => MODULE_KEY_TO_SHORT_KEY[k] === change.moduleKey
              );
              const title = moduleTitleFor(change.moduleKey, fullKey);
              return (
                <div key={change.moduleKey} className="text-xs">
                  <span className="font-medium text-gray-700">{title}:</span>
                  <span className="text-green-700 font-medium ml-1">
                    {formatDateOrNotSet(change.newDate)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom-right CTA — Documents when a re-finalise is in flight, else Done. */}
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={handleDone}
            className="px-4 py-2 text-xs font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
          >
            {goToDocuments ? 'Go to Documents Dashboard' : 'Done'}
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'confirm':
        return renderConfirm();
      case 'success':
        return renderSuccess();
      case 'edit':
      default:
        return renderEdit();
    }
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <AlertDialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out"
        >
          <VisuallyHidden.Root>
            <AlertDialog.Title>Manage Appointment Dates</AlertDialog.Title>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Set or edit appointment dates for assessment modules.
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
            aria-label="Close dialog"
          >
            <XIcon className="w-5 h-5" aria-hidden="true" />
          </button>

          {renderCurrentStep()}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default AppointmentDateDialog;
